import { useState, useEffect } from 'react';
import { Search, MapPin, Star, DollarSign, Clock, Users, Shield, X, SlidersHorizontal } from 'lucide-react';
import { geolocationService } from '@/services/geolocationService';
import { backendJobService } from '@/services/backendJobService';
import type { JobCategory, SearchFilters } from '@/types';

interface AdvancedSearchProps {
  onSearch: (filters: SearchFilters) => void;
  initialFilters?: Partial<SearchFilters>;
  type: 'jobs' | 'providers';
  className?: string;
}

const defaultFilters: SearchFilters = {
  query: '',
  category: '',
  location: '',
  radius: 25,
  priceRange: { min: 0, max: 1000 },
  minRating: 0,
  availability: 'any',
  providerType: 'any',
  verified: false,
  premium: false,
  emergency: false,
  sortBy: 'relevance'
};

const fallbackCategories = [
  'Plumbing', 'Electrical', 'Cleaning', 'Gardening', 'Painting',
  'Carpentry', 'Moving', 'Handyman', 'Appliance Repair', 'HVAC'
];

const locations = [
  'Dublin 1', 'Dublin 2', 'Dublin 4', 'Dublin 6', 'Dublin 8',
  'Cork', 'Galway', 'Limerick', 'Waterford'
];

export function AdvancedSearch({ onSearch, initialFilters, type, className = '' }: AdvancedSearchProps) {
  const [filters, setFilters] = useState<SearchFilters>({
    ...defaultFilters,
    ...initialFilters
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [categories, setCategories] = useState<string[]>(fallbackCategories);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Load categories from database
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const dbCategories = await backendJobService.getJobCategories();
        if (dbCategories && dbCategories.length > 0) {
          const categoryNames = dbCategories.map((cat: JobCategory) => cat.name);
          setCategories(categoryNames);
        } else {
          console.warn('⚠️ No categories from API, using fallback');
          setCategories(fallbackCategories);
        }
      } catch (error) {
        console.error('Failed to load categories:', error);
        setCategories(fallbackCategories);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  // Remove automatic search on filter change to prevent infinite loops
  // useEffect(() => {
  //   onSearch(filters);
  // }, [filters, onSearch]);

  const updateFilter = <K extends keyof SearchFilters>(
    key: K,
    value: SearchFilters[K]
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
  };

  const getCurrentLocation = async () => {
    setIsGettingLocation(true);
    try {
      const location = await geolocationService.getCurrentPosition();
      // setUserLocation(location.address); // Commented out - not used
      updateFilter('location', location.address);
    } catch (error) {
      console.error('Failed to get location:', error);
    } finally {
      setIsGettingLocation(false);
    }
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.query) count++;
    if (filters.category) count++;
    if (filters.location) count++;
    if (filters.minRating && filters.minRating > 0) count++;
    if (filters.priceRange && (filters.priceRange.min > 0 || filters.priceRange.max < 1000)) count++;
    if (filters.availability !== 'any') count++;
    if (filters.providerType !== 'any') count++;
    if (filters.verified) count++;
    if (filters.premium) count++;
    if (filters.emergency) count++;
    return count;
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
      {/* Basic Search */}
      <div className="space-y-4">
        <div className="flex space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={type === 'jobs' ? 'Search jobs...' : 'Search providers...'}
              value={filters.query}
              onChange={(e) => updateFilter('query', e.target.value)}
              className="input pl-10 w-full"
              data-testid="search-query"
            />
          </div>

          <button
            onClick={() => onSearch(filters)}
            className="btn btn-primary flex items-center"
            data-testid="search-button"
          >
            <Search className="w-4 h-4 mr-2" />
            Search
          </button>

          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`btn ${showAdvanced ? 'btn-primary' : 'btn-outline'} flex items-center`}
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filters
            {getActiveFilterCount() > 0 && (
              <span className="ml-2 bg-primary-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {getActiveFilterCount()}
              </span>
            )}
          </button>
        </div>

        {/* Quick Filters */}
        <div className="flex flex-wrap gap-2">
          <select
            value={filters.category}
            onChange={(e) => updateFilter('category', e.target.value)}
            className="select select-bordered select-sm"
            disabled={loadingCategories}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <div className="flex items-center space-x-2">
            <select
              value={filters.location}
              onChange={(e) => updateFilter('location', e.target.value)}
              className="select select-bordered select-sm"
              data-testid="search-location"
            >
              <option value="">All Locations</option>
              {locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>

            <button
              onClick={getCurrentLocation}
              disabled={isGettingLocation}
              className="btn btn-outline btn-sm flex items-center"
              title="Use my location"
            >
              <MapPin className={`w-4 h-4 ${isGettingLocation ? 'animate-pulse' : ''}`} />
            </button>
          </div>

          <select
            value={filters.sortBy}
            onChange={(e) => updateFilter('sortBy', e.target.value as SearchFilters['sortBy'])}
            className="select select-bordered select-sm"
          >
            <option value="relevance">Most Relevant</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
            <option value="rating">Highest Rated</option>
            <option value="distance">Nearest First</option>
            <option value="reviews">Most Reviews</option>
          </select>
        </div>
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Price Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <DollarSign className="w-4 h-4 inline mr-1" />
                Price Range (€)
              </label>
              <div className="space-y-2">
                <div className="flex space-x-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.priceRange?.min ?? ''}
                    onChange={(e) => updateFilter('priceRange', {
                      min: parseInt(e.target.value) || 0,
                      max: filters.priceRange?.max ?? 1000
                    })}
                    className="input input-sm flex-1"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.priceRange?.max === 1000 ? '' : (filters.priceRange?.max ?? '')}
                    onChange={(e) => updateFilter('priceRange', {
                      min: filters.priceRange?.min ?? 0,
                      max: parseInt(e.target.value) || 1000
                    })}
                    className="input input-sm flex-1"
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="1000"
                  step="25"
                  value={filters.priceRange?.max ?? 1000}
                  onChange={(e) => updateFilter('priceRange', {
                    min: filters.priceRange?.min ?? 0,
                    max: parseInt(e.target.value)
                  })}
                  className="w-full"
                />
              </div>
            </div>

            {/* Distance */}
            {filters.location && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Distance ({filters.radius} km)
                </label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={filters.radius}
                  onChange={(e) => updateFilter('radius', parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 km</span>
                  <span>50 km</span>
                </div>
              </div>
            )}

            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Star className="w-4 h-4 inline mr-1" />
                Minimum Rating
              </label>
              <div className="flex space-x-2">
                {[0, 3, 4, 4.5, 5].map(rating => (
                  <button
                    key={rating}
                    onClick={() => updateFilter('minRating', rating)}
                    className={`btn btn-sm ${filters.minRating === rating ? 'btn-primary' : 'btn-outline'
                      }`}
                  >
                    {rating === 0 ? 'Any' : `${rating}+`}
                    {rating > 0 && <Star className="w-3 h-3 ml-1" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Availability */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Availability
              </label>
              <select
                value={filters.availability}
                onChange={(e) => updateFilter('availability', e.target.value as SearchFilters['availability'])}
                className="select select-bordered w-full"
              >
                <option value="any">Any Time</option>
                <option value="now">Available Now</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
              </select>
            </div>

            {/* Provider Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                Provider Type
              </label>
              <select
                value={filters.providerType}
                onChange={(e) => updateFilter('providerType', e.target.value as SearchFilters['providerType'])}
                className="select select-bordered w-full"
              >
                <option value="any">Any Type</option>
                <option value="individual">Individual</option>
                <option value="company">Company</option>
              </select>
            </div>

            {/* Special Features */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Shield className="w-4 h-4 inline mr-1" />
                Special Features
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.verified}
                    onChange={(e) => updateFilter('verified', e.target.checked)}
                    className="checkbox checkbox-sm mr-2"
                  />
                  <span className="text-sm">Verified Only</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.premium}
                    onChange={(e) => updateFilter('premium', e.target.checked)}
                    className="checkbox checkbox-sm mr-2"
                  />
                  <span className="text-sm">Premium Providers</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.emergency}
                    onChange={(e) => updateFilter('emergency', e.target.checked)}
                    className="checkbox checkbox-sm mr-2"
                  />
                  <span className="text-sm">Emergency Service</span>
                </label>
              </div>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              {getActiveFilterCount()} filter{getActiveFilterCount() !== 1 ? 's' : ''} applied
            </div>

            <div className="flex space-x-3">
              <button
                onClick={resetFilters}
                className="btn btn-outline btn-sm flex items-center"
              >
                <X className="w-4 h-4 mr-1" />
                Clear All
              </button>

              <button
                onClick={() => setShowAdvanced(false)}
                className="btn btn-primary btn-sm"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}